import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

export default function NationalityManager({ api, getAuthHeader }) {
  const [nationalities, setNationalities] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nationality_name: '',
    eligible: true,
    requirements: '',
    notes: '',
    extra_fee: 0,
    is_visa_free: false,
    has_on_arrival_30: false,
    has_on_arrival_90: false,
    has_on_arrival_180: false
  });

  useEffect(() => {
    fetchNationalities();
  }, []);

  const fetchNationalities = async () => {
    try {
      const response = await axios.get(`${api}/nationalities`);
      setNationalities(response.data);
    } catch (error) {
      toast.error('Failed to fetch nationalities');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${api}/nationalities/${editingId}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Nationality updated successfully');
      } else {
        await axios.post(`${api}/nationalities`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Nationality added successfully');
      }
      resetForm();
      fetchNationalities();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const handleEdit = (nationality) => {
    setFormData({
      nationality_name: nationality.nationality_name,
      eligible: nationality.eligible,
      requirements: nationality.requirements,
      notes: nationality.notes,
      extra_fee: nationality.extra_fee,
      is_visa_free: nationality.is_visa_free || false,
      has_on_arrival_30: nationality.has_on_arrival_30 || false,
      has_on_arrival_90: nationality.has_on_arrival_90 || false,
      has_on_arrival_180: nationality.has_on_arrival_180 || false
    });
    setEditingId(nationality.id);
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this nationality?')) return;
    try {
      await axios.delete(`${api}/nationalities/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Nationality deleted successfully');
      fetchNationalities();
    } catch (error) {
      toast.error('Failed to delete nationality');
    }
  };

  const resetForm = () => {
    setFormData({
      nationality_name: '',
      eligible: true,
      requirements: '',
      notes: '',
      extra_fee: 0,
      is_visa_free: false,
      has_on_arrival_30: false,
      has_on_arrival_90: false,
      has_on_arrival_180: false
    });
    setEditingId(null);
    setShowAdd(false);
  };

  const filteredNationalities = nationalities.filter((nat) =>
    nat.nationality_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Manage Nationalities</h2>
          <p className="text-slate-600 mt-1">Total: {nationalities.length}</p>
        </div>
        <Button
          data-testid="add-nationality-btn"
          onClick={() => setShowAdd(!showAdd)}
          className="btn-primary flex items-center gap-2"
        >
          {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAdd ? 'Cancel' : 'Add Nationality'}
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Nationality' : 'Add New Nationality'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label-text">Nationality Name *</label>
                  <Input
                    data-testid="nationality-name-input"
                    value={formData.nationality_name}
                    onChange={(e) => setFormData({ ...formData, nationality_name: e.target.value })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="label-text">Extra Fee (AED)</label>
                  <Input
                    data-testid="nationality-extra-fee-input"
                    type="number"
                    step="0.01"
                    value={formData.extra_fee}
                    onChange={(e) => setFormData({ ...formData, extra_fee: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                  />
                </div>
              </div>
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="nationality-eligible-switch"
                    checked={formData.eligible}
                    onCheckedChange={(checked) => setFormData({ ...formData, eligible: checked })}
                  />
                  <label className="text-sm font-medium text-slate-700">
                    Eligible for UAE Visa (No = Shows "Contact Us" message)
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="nationality-visa-free-switch"
                    checked={formData.is_visa_free}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visa_free: checked })}
                  />
                  <label className="text-sm font-medium text-slate-700">
                    Visa-Free Entry (GCC Countries)
                  </label>
                </div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                <p className="text-sm font-bold text-slate-700">On-Arrival Visa Options (Select applicable durations):</p>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="nationality-on-arrival-30-switch"
                    checked={formData.has_on_arrival_30}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_on_arrival_30: checked })}
                  />
                  <label className="text-sm font-medium text-slate-700">
                    30 Days On-Arrival
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="nationality-on-arrival-90-switch"
                    checked={formData.has_on_arrival_90}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_on_arrival_90: checked })}
                  />
                  <label className="text-sm font-medium text-slate-700">
                    90 Days On-Arrival
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    data-testid="nationality-on-arrival-180-switch"
                    checked={formData.has_on_arrival_180}
                    onCheckedChange={(checked) => setFormData({ ...formData, has_on_arrival_180: checked })}
                  />
                  <label className="text-sm font-medium text-slate-700">
                    180 Days On-Arrival (e.g., Mexico)
                  </label>
                </div>
              </div>
              <div>
                <label className="label-text">Requirements</label>
                <Textarea
                  data-testid="nationality-requirements-input"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  className="input-field min-h-[100px]"
                  placeholder="Enter specific requirements for this nationality"
                />
              </div>
              <div>
                <label className="label-text">Notes</label>
                <Textarea
                  data-testid="nationality-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field min-h-[80px]"
                  placeholder="Additional notes"
                />
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="btn-primary flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingId ? 'Update' : 'Add'}
                </Button>
                <Button type="button" onClick={resetForm} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div>
        <Input
          data-testid="nationality-search-input"
          placeholder="Search nationalities..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-field max-w-md"
        />
      </div>

      {/* List */}
      <div className="grid gap-4">
        {filteredNationalities.map((nationality) => (
          <Card key={nationality.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="text-xl font-bold text-slate-900">{nationality.nationality_name}</h3>
                    {nationality.is_visa_free ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                        🎉 Visa-Free (GCC)
                      </span>
                    ) : (
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          nationality.eligible ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {nationality.eligible ? 'Eligible' : 'Special Processing'}
                      </span>
                    )}
                    {nationality.has_on_arrival_30 && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        ℹ️ 30 Days On-Arrival
                      </span>
                    )}
                    {nationality.has_on_arrival_90 && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        ℹ️ 90 Days On-Arrival
                      </span>
                    )}
                    {nationality.has_on_arrival_180 && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                        ℹ️ 180 Days On-Arrival
                      </span>
                    )}
                    {nationality.extra_fee > 0 && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                        +AED {nationality.extra_fee}
                      </span>
                    )}
                  </div>
                  {(nationality.has_on_arrival_30 || nationality.has_on_arrival_90 || nationality.has_on_arrival_180) && (
                    <p className="text-sm text-blue-700 mb-1">
                      <strong>On-Arrival Options:</strong> 
                      {nationality.has_on_arrival_30 && ' 30 days'}
                      {nationality.has_on_arrival_90 && ' 90 days'}
                      {nationality.has_on_arrival_180 && ' 180 days'}
                    </p>
                  )}
                  {nationality.requirements && (
                    <p className="text-sm text-slate-600 mb-1">
                      <strong>Requirements:</strong> {nationality.requirements}
                    </p>
                  )}
                  {nationality.notes && (
                    <p className="text-sm text-slate-600">
                      <strong>Notes:</strong> {nationality.notes}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-nationality-${nationality.id}`}
                    onClick={() => handleEdit(nationality)}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-nationality-${nationality.id}`}
                    onClick={() => handleDelete(nationality.id)}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
